import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Button } from "@/shared/components/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import { logger } from "@/infrastructure/lib/logger";
import { Copy, Check } from "lucide-react";

interface McpIntegrationInfo {
  id: string;
  name: string;
  label?: string | null;
  logo_url?: string | null;
  tools: Array<{ name: string; description?: string }>;
}

interface McpToolsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mcpIntegrations: McpIntegrationInfo[];
}

const CORE_TOOL_GROUPS: Array<{ titleKey: string; tools: string[] }> = [
  {
    titleKey: "playground.mcpTools.coreGroups.contextKnowledge",
    tools: ["get_my_profile", "get_workspace_info", "search_knowledge", "update_my_ai_preferences"],
  },
  {
    titleKey: "playground.mcpTools.coreGroups.agents",
    tools: ["list_agents", "invoke_agent"],
  },
  {
    titleKey: "playground.mcpTools.coreGroups.integrations",
    tools: ["list_integration_catalog", "list_installed_integrations", "get_integration", "install_integration"],
  },
  {
    titleKey: "playground.mcpTools.coreGroups.admin",
    tools: ["create_integration", "update_integration", "delete_integration", "get_mcp_settings", "update_mcp_settings"],
  },
];

export function McpToolsDialog({ open, onOpenChange, mcpIntegrations }: McpToolsDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const configSnippet = useMemo(
    () =>
      JSON.stringify(
        {
          mcpServers: {
            prodgy: {
              type: "http",
              url: `${window.location.origin}/mcp`,
              headers: { Authorization: "Bearer prodgy_your_token_here" },
            },
          },
        },
        null,
        2,
      ),
    [],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(configSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error: unknown) {
      const err = error as Error;
      logger.error("Failed to copy MCP config snippet", { error: err?.message });
      toast({ variant: "destructive", title: t("playground.mcpTools.config.copyFailed") });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("playground.mcpTools.title")}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="integrations" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="w-fit justify-start self-start">
            <TabsTrigger value="integrations">{t("playground.mcpTools.tabs.integrations")}</TabsTrigger>
            <TabsTrigger value="core">{t("playground.mcpTools.tabs.core")}</TabsTrigger>
            <TabsTrigger value="config">{t("playground.mcpTools.tabs.config")}</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4 pr-1 min-h-0">
            {/* ── Integrations ── */}
            <TabsContent value="integrations" className="mt-0 space-y-3">
              {mcpIntegrations.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  {t("playground.mcpTools.integrations.empty")}
                </p>
              ) : (
                mcpIntegrations.map((integration) => {
                  const displayName = integration.label ?? integration.name;
                  return (
                    <div key={integration.id} className="rounded-md border p-3">
                      <div className="flex items-center gap-2 mb-2">
                        {integration.logo_url ? (
                          <img
                            src={integration.logo_url}
                            alt={displayName}
                            className="h-5 w-5 object-contain"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-muted text-[10px] font-medium">
                            {displayName[0]?.toUpperCase()}
                          </span>
                        )}
                        <span className="font-medium text-sm">{displayName}</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {t("playground.mcpTools.integrations.toolCount", {
                            count: integration.tools.length,
                          })}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {integration.tools.map((tool) => (
                          <span
                            key={tool.name}
                            className="inline-block px-1.5 py-0.5 text-[10px] bg-muted rounded text-muted-foreground font-mono"
                            title={tool.description || undefined}
                          >
                            {tool.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </TabsContent>

            {/* ── Core Tools ── */}
            <TabsContent value="core" className="mt-0 space-y-4">
              {CORE_TOOL_GROUPS.map((group) => (
                <div key={group.titleKey}>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    {t(group.titleKey)}
                  </h4>
                  <div className="space-y-1.5">
                    {group.tools.map((toolName) => (
                      <div key={toolName} className="flex flex-col gap-0.5 p-2 rounded border border-border/40">
                        <code className="text-xs font-mono">{toolName}</code>
                        <span className="text-xs text-muted-foreground">
                          {t(`playground.mcpTools.coreTools.${toolName}`, toolName)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* ── Config ── */}
            <TabsContent value="config" className="mt-0 space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("playground.mcpTools.config.descriptionPrefix")}
                <code className="text-xs">.mcp.json</code>, <code className="text-xs">.cursor/mcp.json</code>
                {t("playground.mcpTools.config.descriptionSuffix")}
              </p>
              <div className="relative rounded-md border bg-muted/30">
                <pre className="text-xs p-3 overflow-x-auto font-mono">{configSnippet}</pre>
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-1.5 right-1.5 h-7"
                  onClick={handleCopy}
                  aria-label={copied ? t("playground.mcpTools.config.copied") : t("playground.mcpTools.config.copy")}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  <span className="ml-1 text-xs">
                    {copied ? t("playground.mcpTools.config.copied") : t("playground.mcpTools.config.copy")}
                  </span>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("playground.mcpTools.config.tokenHintPrefix")}
                <strong>{t("playground.mcpTools.config.tokenHintBold")}</strong>
                {t("playground.mcpTools.config.tokenHintSuffix")}
              </p>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
